import React, { useState, useEffect } from 'react';
import { BlueGovLayout } from '../layouts/BlueGovLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart
} from 'recharts';
import {
  Cpu, DollarSign, TrendingUp, Users, Building2, Brain, AlertTriangle,
  Download, RefreshCw, Zap, Clock, Target, BarChart3, PieChart as PieChartIcon,
  Lightbulb, FileText, Filter, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1d4ed8', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 px-4 py-3 rounded-xl shadow-2xl border border-slate-700">
        <p className="text-slate-400 text-xs mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-white text-sm">
            {p.name}: <span className="font-bold">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};

const TokenIntelligencePage = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [tenantRanking, setTenantRanking] = useState([]);
  const [userRanking, setUserRanking] = useState([]);
  const [moduleRanking, setModuleRanking] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insights, setInsights] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pricing, setPricing] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('bluegov_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/token-intelligence/dashboard?period=${period}`, { headers: getAuthHeaders() });
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchRankings = async () => {
    try {
      const [tenants, users, modules] = await Promise.all([
        fetch(`${API_URL}/api/token-intelligence/ranking/tenants?period=${period}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/token-intelligence/ranking/users?period=${period}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/token-intelligence/ranking/modules?period=${period}`, { headers: getAuthHeaders() })
      ]);
      if (tenants.ok) setTenantRanking(await tenants.json());
      if (users.ok) setUserRanking(await users.json());
      if (modules.ok) setModuleRanking(await modules.json());
    } catch (e) { console.error(e); }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/token-intelligence/alerts`, { headers: getAuthHeaders() });
      if (res.ok) setAlerts(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/token-intelligence/insights`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInsights(data.insights || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/token-intelligence/audit?limit=100`, { headers: getAuthHeaders() });
      if (res.ok) setAuditLogs(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/token-intelligence/pricing`, { headers: getAuthHeaders() });
      if (res.ok) setPricing(await res.json());
    } catch (e) { console.error(e); }
  };

  const seedDemoData = async () => {
    try {
      toast.info('Gerando dados de demonstração...');
      const res = await fetch(`${API_URL}/api/token-intelligence/seed-demo`, { method: 'POST', headers: getAuthHeaders() });
      if (res.ok) {
        toast.success('Dados de demonstração gerados!');
        fetchAll();
      }
    } catch (e) { toast.error('Erro ao gerar dados'); }
  };

  const exportData = async (format) => {
    try {
      toast.info(`Exportando ${format.toUpperCase()}...`);
      const res = await fetch(`${API_URL}/api/token-intelligence/export?format=${format}&period=${period}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `token_usage_${period}.${format}`;
        a.click();
        toast.success('Exportação concluída!');
      }
    } catch (e) { toast.error('Erro na exportação'); }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchRankings(), fetchAlerts(), fetchInsights(), fetchAuditLogs(), fetchPricing()]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [period]);

  const summary = dashboard?.summary || {};
  const byDay = dashboard?.by_day || [];
  const byModule = dashboard?.by_module || [];
  const byHour = dashboard?.by_hour || [];
  const forecast = dashboard?.forecast || {};

  return (
    <BlueGovLayout>
      <div className="p-6 space-y-6" data-testid="token-intelligence-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Cpu className="w-7 h-7 text-blue-400" />
              Token Intelligence
            </h1>
            <p className="text-slate-400 mt-1">Monitore consumo, custos e performance de IA</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 dias</SelectItem>
                <SelectItem value="30d">30 dias</SelectItem>
                <SelectItem value="90d">90 dias</SelectItem>
                <SelectItem value="365d">1 ano</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => fetchAll()} className="border-slate-700 text-slate-300">
              <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportData('csv')} className="border-slate-700 text-slate-300">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button size="sm" onClick={seedDemoData} className="bg-blue-600 hover:bg-blue-700">
              <Zap className="w-4 h-4 mr-1" /> Demo
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-600">Dashboard</TabsTrigger>
            <TabsTrigger value="tenants" className="data-[state=active]:bg-blue-600">Clientes</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-600">Usuários</TabsTrigger>
            <TabsTrigger value="modules" className="data-[state=active]:bg-blue-600">Módulos IA</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-blue-600">Alertas</TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-blue-600">Auditoria</TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-blue-600">Pricing</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6 mt-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-blue-300">Tokens</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(summary.total_tokens)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 border-emerald-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-emerald-300">Custo USD</span>
                  </div>
                  <p className="text-2xl font-bold text-white">${summary.total_cost_usd?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-blue-300">Custo BRL</span>
                  </div>
                  <p className="text-2xl font-bold text-white">R${summary.total_cost_brl?.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 border-amber-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-amber-400" />
                    <span className="text-xs text-amber-300">Média/dia</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(summary.avg_tokens_per_day)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-rose-900/50 to-rose-800/30 border-rose-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-rose-400" />
                    <span className="text-xs text-rose-300">Requisições</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{formatNumber(summary.total_requests)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 border-cyan-700/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs text-cyan-300">Usuários</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{summary.unique_users}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-slate-800/50 to-slate-700/30 border-slate-600/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-300">Clientes</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{summary.unique_tenants}</p>
                </CardContent>
              </Card>
            </div>

            {/* Forecast Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Previsão 7 dias</p>
                    <p className="text-xl font-bold text-white">{formatNumber(forecast['7d'])} tokens</p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-emerald-400" />
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Previsão 30 dias</p>
                    <p className="text-xl font-bold text-white">{formatNumber(forecast['30d'])} tokens</p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-emerald-400" />
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Previsão 90 dias</p>
                    <p className="text-xl font-bold text-white">{formatNumber(forecast['90d'])} tokens</p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-amber-400" />
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Usage Chart */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Consumo Diário
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={byDay}>
                      <defs>
                        <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1d4ed8" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#1d4ed8" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="tokens" stroke="#1d4ed8" fill="url(#colorTokens)" name="Tokens" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Module Distribution Pie */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-emerald-400" />
                    Distribuição por Módulo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={byModule.slice(0, 8)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="tokens"
                        nameKey="module"
                        label={({ module, percent }) => `${module?.slice(0, 10)} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#64748b' }}
                      >
                        {byModule.slice(0, 8).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Hourly Heatmap */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    Distribuição por Hora
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={byHour}>
                      <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v) => `${v}h`} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={formatNumber} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="tokens" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Tokens" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Insights */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-400" />
                    Insights Automáticos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.length > 0 ? insights.map((insight, i) => (
                      <div key={i} className="p-3 rounded-lg bg-slate-700/50 text-slate-200 text-sm">
                        {insight}
                      </div>
                    )) : (
                      <p className="text-slate-500 text-center py-4">Clique em "Demo" para gerar dados de exemplo</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Ranking de Clientes</CardTitle>
                <CardDescription className="text-slate-400">Consumo de tokens por cliente</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">#</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Cliente</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">UF</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Tokens</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Custo USD</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Custo BRL</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Requisições</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Usuários</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantRanking.map((t, i) => (
                        <tr key={t.tenant_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4 text-slate-500">{i + 1}</td>
                          <td className="py-3 px-4 text-white font-medium">{t.name}</td>
                          <td className="py-3 px-4 text-slate-300">{t.uf}</td>
                          <td className="py-3 px-4 text-right text-blue-400 font-mono">{formatNumber(t.total_tokens)}</td>
                          <td className="py-3 px-4 text-right text-emerald-400">${t.total_cost_usd}</td>
                          <td className="py-3 px-4 text-right text-blue-400">R${t.total_cost_brl}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{t.requests}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{t.active_users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tenantRanking.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Ranking de Usuários</CardTitle>
                <CardDescription className="text-slate-400">Consumo de tokens por usuário</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">#</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Usuário</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Cliente</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Tokens</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Custo USD</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Requisições</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userRanking.map((u, i) => (
                        <tr key={`${u.user_id}-${i}`} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4 text-slate-500">{i + 1}</td>
                          <td className="py-3 px-4 text-white font-medium">{u.user_id}</td>
                          <td className="py-3 px-4 text-slate-300">{u.user_email}</td>
                          <td className="py-3 px-4 text-slate-300">{u.tenant_id}</td>
                          <td className="py-3 px-4 text-right text-blue-400 font-mono">{formatNumber(u.total_tokens)}</td>
                          <td className="py-3 px-4 text-right text-emerald-400">${u.total_cost_usd}</td>
                          <td className="py-3 px-4 text-right text-slate-300">{u.requests}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {userRanking.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Ranking de Módulos de IA</CardTitle>
                <CardDescription className="text-slate-400">Consumo por funcionalidade de IA</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {moduleRanking.map((m, i) => (
                    <div key={m._id} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS[i % COLORS.length] + '30' }}>
                          <Brain className="w-5 h-5" style={{ color: COLORS[i % COLORS.length] }} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{m._id}</p>
                          <p className="text-slate-400 text-sm">{m.requests} requisições • {m.unique_users} usuários</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono">{formatNumber(m.total_tokens)} tokens</p>
                        <p className="text-emerald-400 text-sm">${m.total_cost_usd}</p>
                        <Badge variant="outline" className="text-xs mt-1" style={{ borderColor: COLORS[i % COLORS.length], color: COLORS[i % COLORS.length] }}>
                          {m.percent}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {moduleRanking.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Alertas Inteligentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.map((alert, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      alert.severity === 'HIGH' ? 'bg-red-900/20 border-red-700' :
                      alert.severity === 'MEDIUM' ? 'bg-amber-900/20 border-amber-700' :
                      'bg-blue-900/20 border-blue-700'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={
                            alert.severity === 'HIGH' ? 'bg-red-600' :
                            alert.severity === 'MEDIUM' ? 'bg-amber-600' : 'bg-blue-600'
                          }>{alert.severity}</Badge>
                          <p className="text-white mt-2">{alert.message}</p>
                          <p className="text-slate-400 text-xs mt-1">{alert.type}</p>
                        </div>
                        {alert.change && (
                          <span className={`font-mono ${alert.change.startsWith('+') ? 'text-red-400' : 'text-emerald-400'}`}>
                            {alert.change}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {alerts.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum alerta no momento</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Log de Auditoria</CardTitle>
                <CardDescription className="text-slate-400">Histórico detalhado de uso de tokens</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-2 px-3 text-slate-400">Timestamp</th>
                        <th className="text-left py-2 px-3 text-slate-400">Cliente</th>
                        <th className="text-left py-2 px-3 text-slate-400">Usuário</th>
                        <th className="text-left py-2 px-3 text-slate-400">Módulo</th>
                        <th className="text-right py-2 px-3 text-slate-400">Input</th>
                        <th className="text-right py-2 px-3 text-slate-400">Output</th>
                        <th className="text-right py-2 px-3 text-slate-400">Total</th>
                        <th className="text-right py-2 px-3 text-slate-400">Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.slice(0, 50).map((log, i) => (
                        <tr key={log.log_id || i} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                          <td className="py-2 px-3 text-slate-400 font-mono">{new Date(log.timestamp).toLocaleString('pt-BR')}</td>
                          <td className="py-2 px-3 text-slate-300">{log.tenant_id}</td>
                          <td className="py-2 px-3 text-slate-300">{log.user_email || log.user_id}</td>
                          <td className="py-2 px-3 text-blue-400">{log.module}</td>
                          <td className="py-2 px-3 text-right text-slate-300">{log.tokens_input?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-slate-300">{log.tokens_output?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-white font-mono">{log.tokens_total?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-emerald-400">${log.cost_usd?.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {auditLogs.length === 0 && (
                    <p className="text-slate-500 text-center py-8">Nenhum log disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Configuração de Pricing</CardTitle>
                <CardDescription className="text-slate-400">Defina custos base e markup</CardDescription>
              </CardHeader>
              <CardContent>
                {pricing && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Modelo</p>
                      <p className="text-white text-xl font-bold">{pricing.model_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Input (USD/1M tokens)</p>
                      <p className="text-white text-xl font-bold">${pricing.input_price_per_1m}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Output (USD/1M tokens)</p>
                      <p className="text-white text-xl font-bold">${pricing.output_price_per_1m}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Câmbio BRL</p>
                      <p className="text-white text-xl font-bold">R${pricing.exchange_rate_brl}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Markup</p>
                      <p className="text-white text-xl font-bold">{pricing.markup_percent}%</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Custo/1K tokens (médio)</p>
                      <p className="text-emerald-400 text-xl font-bold">
                        ${(((pricing.input_price_per_1m + pricing.output_price_per_1m) / 2) / 1000 * (1 + pricing.markup_percent/100)).toFixed(4)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </BlueGovLayout>
  );
};

export default TokenIntelligencePage;
